import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Autocomplete, Box, Checkbox, Chip, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, TextField, Typography,} from "@mui/material";
import Dialog from "../../../../../../../components/base/dialog";
import Button from "../../../../../../../components/base/button/button";
import useSavedComponentsState from "../../../../../../../hooks/use-saved-components-state";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import AutocompleteBase from "../../../../../../../components/text-fields/autocomplete/autocomplete-base";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Switch} from "../../../../../../../components/base/switch";

// ─── Field renderer ───────────────────────────────────────────────────────────

/**
 * Renders a single interactive field (autocomplete / input / checkbox).
 *
 * @param {Object}  props
 * @param {import('../../../../../../../core/services/api/api').InteractiveField} props.field
 * @param {any}     props.value          - Current controlled value for this field
 * @param {Array}   props.effectiveOptions - Already-filtered options (cascade handled by parent)
 * @param {Function} props.onChange       - fn(newValue) callback
 */
const InteractiveFieldRenderer = ({field, value, effectiveOptions, onChange}) => {
    if (field.type === 'autocomplete') {
        if (field.getOptions) {
            return (
                <AutocompleteBase
                    fullWidth
                    getOptions={field.getOptions}
                    value={value ?? null}
                    onChange={(_, newVal) => onChange(newVal)}
                    label={field.label}
                    textFieldProps={{
                        variant: 'outlined',
                        size: 'small',
                    }}
                    {...(field.autocompleteProps ?? {})}
                />
            );
        }
        return (
            <Autocomplete
                fullWidth
                options={effectiveOptions ?? []}
                value={value ?? null}
                onChange={(_, newVal) => onChange(newVal)}
                {...(field.autocompleteProps ?? {})}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={field.label}
                        variant={'outlined'}
                        size={'small'}
                    />
                )}
            />
        );
    }

    if (field.type === 'checkbox') {
        return (
            <FormControlLabel
                control={
                    <Checkbox
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...(field.inputProps ?? {})}
                    />
                }
                label={field.label}
            />
        );
    }

    // Default: text input
    return (
        <TextField
            fullWidth
            label={field.label}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            variant={'outlined'}
            size={'small'}
            {...(field.inputProps ?? {})}
        />
    );
};

// ─── Section renderer ─────────────────────────────────────────────────────────

/**
 * Renders one triggered section card with its fields and current-value summary.
 *
 * @param {Object}  props
 * @param {import('../../engine/corrective-interactions').InteractiveSectionConfig & {fieldOptions: Object}} props.section
 * @param {Object}  props.fieldValues          - { [fieldKey]: selectedValue } for this section
 * @param {Function} props.onFieldChange       - fn(fieldKey, newValue)
 * @param {Object}  props.originalApiRequest
 */
const InteractiveSectionCard = ({section, fieldValues, onFieldChange, originalApiRequest}) => {
    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                backgroundColor: 'background.paper',
            }}
        >
            {/* Section heading */}
            <Typography variant={'subtitle2'} fontWeight={700} color={'primary'} mb={0.5}>
                {section.sectionTitle}
            </Typography>

            {/* Current value summary */}
            <Typography variant={'caption'} color={'text.secondary'} component={'div'} mb={2}>
                Current values: <em>{section.describeRecord(originalApiRequest)}</em>
            </Typography>

            {/* Fields */}
            {section.fields.map((field) => {
                // Cascade filtering: if this field depends on another field's value,
                // filter available options based on the filterOption predicate.
                const effectiveOptions = field.cascadeSourceKey
                    ? (section.fieldOptions?.[field.key] ?? []).filter(
                        (opt) =>
                            !field.filterOption ||
                            field.filterOption(opt, fieldValues[field.cascadeSourceKey], section.fieldOptions)
                    )
                    : (section.fieldOptions?.[field.key] ?? []);

                return (
                    <Box key={field.key} mb={1.5}>
                        <InteractiveFieldRenderer
                            field={field}
                            value={fieldValues[field.key]}
                            effectiveOptions={effectiveOptions}
                            onChange={(newVal) => onFieldChange(field.key, newVal)}
                        />
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Dialog ───────────────────────────────────────────────────────────────────

/**
 * InteractiveCorrectionDialog
 *
 * Shown when the import loop encounters a correctable error.
 * Renders one section card per triggered InteractiveSectionConfig.
 * The dialog is purely presentational: all mutation and propagation
 * logic lives in useInteractiveCorrection.
 *
 * @param {Object}   props
 * @param {boolean}  props.open
 * @param {Object|null} props.dialogState  { apiRequest: Object, triggeredSections: (InteractiveSectionConfig & { fieldOptions })[] }
 * @param {Function} props.onApplyAndRetry ({ fieldValuesBySectionKey, applyToSimilar, originalApiRequest }) => void
 * @param {Function} props.onSkip ({ suppressSimilar, originalApiRequest }) => void
 * @param {boolean}  props.interactiveCorrectionEnabled
 * @param {Function} props.setInteractiveCorrectionEnabled
 */
const InteractiveCorrectionDialog = ({
                                         open,
                                         dialogState,
                                         onApplyAndRetry,
                                         onSkip,
                                         interactiveCorrectionEnabled,
                                         setInteractiveCorrectionEnabled,
                                     }) => {
    const {apiRequest, triggeredSections} = dialogState ?? {};

    /**
     * { [sectionKey]: { [fieldKey]: selectedValue } }
     * Initialize each field with the current value from apiRequest so the user
     * sees the existing (failing) state and can modify only what is needed.
     */
    const [fieldValuesBySectionKey, setFieldValuesBySectionKey] = useState({});
    const [applyToSimilar, setApplyToSimilar] = useState(false);
    const [suppressSimilar, setSuppressSimilar] = useState(false);
    const isMounted = useIsMounted();

    const {highestVersion: getCachedDialogStates, save: saveCachedDialogStates} = useSavedComponentsState({
        name: "settings-import-interactive-correction-dialog",
        version: "1.0.0"
    });

    /**
     * Reset state whenever the dialog opens with a new record (safeguard against stale state if the same dialog instance is reused for multiple records in a loop).
     */
    useEffect(() => {
        if (!open || !triggeredSections?.length || !apiRequest) return;

        const initial = {};
        for (const section of triggeredSections) {
            initial[section.sectionKey] = {};
            for (const field of section.fields) {
                // Start with undefined so the user explicitly selects a new value.
                // Undefined values will be ignored during applyAndRetry, preserving previous corrections.
                initial[section.sectionKey][field.key] = undefined;
            }
        }
        setFieldValuesBySectionKey(initial);

        // Restore default values from cache instead of hardcoding false
        void getCachedDialogStates().then((cached) => {
            if (!isMounted())
                return;
            if (cached?.data) {
                setApplyToSimilar(Boolean(cached.data.applyToSimilar));
                setSuppressSimilar(Boolean(cached.data.suppressSimilar));
            } else {
                setApplyToSimilar(false);
                setSuppressSimilar(false);
            }
        });
    }, [open, apiRequest, triggeredSections, getCachedDialogStates, isMounted]);

    /**
     * Updates the "apply to similar" preference and saves it to cache for future dialogs.
     * @type {(function(*): void)|*}
     */
    const handleApplyToSimilarChange = useCallback((checked) => {
        setApplyToSimilar(checked);
        void saveCachedDialogStates({applyToSimilar: checked, suppressSimilar});
    }, [saveCachedDialogStates, suppressSimilar]);

    /**
     * Updates the "suppress similar" preference and saves it to cache for future dialogs.
     * @type {(function(*): void)|*}
     */
    const handleSuppressSimilarChange = useCallback((checked) => {
        setSuppressSimilar(checked);
        void saveCachedDialogStates({applyToSimilar, suppressSimilar: checked});
    }, [saveCachedDialogStates, applyToSimilar]);


    /**
     * Updates the value of a specific field in a specific section.
     * @type {(function(*, *, *): void)|*}
     */
    const handleFieldChange = useCallback((sectionKey, fieldKey, newValue) => {
        setFieldValuesBySectionKey((prev) => ({
            ...prev,
            [sectionKey]: {
                ...(prev[sectionKey] ?? {}),
                [fieldKey]: newValue,
            },
        }));
    }, []);

    /**
     * Called by the dialog's "Apply and Retry" button. Passes all user inputs to the parent callback for processing.
     * @type {(function(): void)|*}
     */
    const handleApplyAndRetry = useCallback(() => {
        onApplyAndRetry?.({
            fieldValuesBySectionKey,
            applyToSimilar,
            originalApiRequest: apiRequest,
        });
    }, [onApplyAndRetry, fieldValuesBySectionKey, applyToSimilar, apiRequest]);

    /**
     * Called by the dialog's "Proceed without changes" button. Passes user suppression preference to the parent callback for processing.
     * @type {(function(): void)|*}
     */
    const handleSkip = useCallback(() => {
        onSkip?.({
            suppressSimilar,
            originalApiRequest: apiRequest,
        });
    }, [onSkip, suppressSimilar, apiRequest]);

    /** True when at least one section has at least one field with a non-null value */
    const hasAnyInput = useMemo(() => {
        if (!triggeredSections) return false;
        return triggeredSections.some((s) =>
            s.fields.some((f) => fieldValuesBySectionKey[s.sectionKey]?.[f.key] != null)
        );
    }, [triggeredSections, fieldValuesBySectionKey]);

    const sectionCount = triggeredSections?.length ?? 0;

    return (
        <Dialog
            open={open}
            setOpen={() => {
                /* Cannot be closed by clicking outside — user must choose */
            }}
            disabled
            loading
            maxWidth={'sm'}
            fullWidth
            disableEscapeKeyDown
        >
            {/* ── Title ── */}
            <DialogTitle component={'div'}>
                <Grid2 container alignItems={'center'} justifyContent={'space-between'} spacing={1}>
                    <Grid2 xs>
                        <Typography variant={'h6'} fontWeight={700} color={'error.main'}>
                            Correction Required <Chip
                            component={'span'}
                            label={sectionCount === 1 ? `${sectionCount} issue` : `${sectionCount} issues`}
                            color={'warning'}
                            size={'small'}
                        />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={'auto'}>
                        <FormControlLabel
                            sx={{m: 0}}
                            control={
                                <Switch
                                    checked={interactiveCorrectionEnabled}
                                    onChange={(e) => setInteractiveCorrectionEnabled?.(e)}
                                    color={'secondary'}
                                    size={5}
                                />
                            }
                            label={
                                <Typography
                                    variant={'body2'}
                                    color={'text.secondary'}
                                    sx={{
                                        marginLeft: 1,
                                        fontSize: (theme) => `${theme.typography.pxToRem(14)} !important`,
                                        fontWeight: `${400} !important`,
                                    }}
                                >
                                    Interactive corrections
                                </Typography>
                            }
                        />
                    </Grid2>
                    <Grid2 xs={12} alignItems={'center'}>
                        <Typography variant={'body2'} color={'text.secondary'} mt={0.5}>
                            {sectionCount === 1
                                ? 'The following record failed to import. Please correct the values below and retry.'
                                : `${sectionCount} issues were detected on this record. Please review each section below.`
                            }
                        </Typography>
                    </Grid2>
                </Grid2>
            </DialogTitle>

            {/* ── Content ── */}
            <DialogContent dividers>
                {
                    triggeredSections?.map((section) =>
                        <InteractiveSectionCard
                            key={section.sectionKey}
                            section={section}
                            fieldValues={fieldValuesBySectionKey[section.sectionKey] ?? {}}
                            onFieldChange={(fieldKey, newVal) =>
                                handleFieldChange(section.sectionKey, fieldKey, newVal)
                            }
                            originalApiRequest={apiRequest}
                        />
                    )
                }

                <Divider sx={{my: 2}}/>

                {/* ── Global checkbox: apply to similar ── */}
                <FormControlLabel
                    sx={{mt: 0.5, ml: 0.5, display: 'flex', alignItems: 'flex-start'}}
                    control={
                        <Checkbox
                            checked={applyToSimilar}
                            onChange={(e) => handleApplyToSimilarChange(e.target.checked)}
                            color={'primary'}
                            size={'small'}
                        />
                    }
                    label={
                        <Typography variant={'body2'}>
                            Apply for similar occurrences
                            <Typography
                                component={'span'}
                                variant={'caption'}
                                color={'text.secondary'}
                                ml={0.5}
                            >
                                (updates all remaining records with the same original values)
                            </Typography>
                        </Typography>
                    }
                />
            </DialogContent>

            {/* ── Actions ── */}
            <DialogActions
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    p: 2,
                }}
            >
                {/* Primary: Apply and Retry */}
                <Button
                    variant={'contained'}
                    color={'primary'}
                    fullWidth
                    disabled={!hasAnyInput}
                    onClick={handleApplyAndRetry}
                >
                    Apply and Retry
                </Button>

                {/* Secondary: Proceed without changes */}
                <Box sx={{ml: '0 !important'}}>
                    <Button
                        variant={'outlined'}
                        color={'inherit'}
                        fullWidth
                        onClick={handleSkip}
                    >
                        Proceed without changes
                    </Button>
                    <FormControlLabel
                        sx={{mt: 0.5, ml: 0.5}}
                        control={
                            <Checkbox
                                checked={suppressSimilar}
                                onChange={(e) => handleSuppressSimilarChange(e.target.checked)}
                                color={'default'}
                                size={'small'}
                            />
                        }
                        label={
                            <Typography variant={'caption'} color={'text.secondary'}>
                                Suppress similar errors going forward (skip without prompting)
                            </Typography>
                        }
                    />
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default InteractiveCorrectionDialog;
